import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getLeaderboard, seedDemoData, getStats } from './agentAPI';
import { useAgent } from './AgentContext';
import { useTheme } from './ThemeContext';
import { CHALLENGES } from './challengeRegistry';
import './Arena.css';

const TOTAL_PTS = CHALLENGES.reduce((s, c) => s + c.points, 0);

const CATEGORY_BREAKDOWN = [
  { icon: 'F', name: 'Form Interaction',  count: 3, pts: 45  },
  { icon: 'D', name: 'Data Grid',         count: 3, pts: 45  },
  { icon: 'N', name: 'Navigation',        count: 3, pts: 45  },
  { icon: 'X', name: 'Drag & Drop',       count: 2, pts: 35  },
  { icon: 'C', name: 'Conditional Logic', count: 2, pts: 40  },
  { icon: 'S', name: 'Search & Filter',   count: 2, pts: 50  },
  { icon: 'R', name: 'Reasoning',         count: 2, pts: 55  },
  { icon: 'A', name: 'Adversarial',       count: 1, pts: 30  },
  { icon: 'L', name: 'Live State',        count: 1, pts: 35  },
  { icon: 'E', name: 'Error Recovery',    count: 1, pts: 30  },
];

const DIFF = [
  { label: 'Easy',   color: '#22c55e', count: 3,  pts: 30  },
  { label: 'Medium', color: '#f59e0b', count: 6,  pts: 90  },
  { label: 'Hard',   color: '#ef4444', count: 6,  pts: 120 },
  { label: 'Expert', color: '#a855f7', count: 5,  pts: 160 },
];

const CAT_ICONS = {
  'Form Interaction': 'SYM_FORM',
  'Data Grid': 'SYM_GRID',
  'Navigation': 'SYM_NAV',
  'Drag & Drop': 'SYM_DRAG',
  'Conditional Logic': 'SYM_COND',
  'Search & Filter': 'SYM_SRCH',
  'Reasoning': 'SYM_BRAIN',
  'Adversarial': 'SYM_ADV',
  'Live State': 'SYM_LIVE',
  'Error Recovery': 'SYM_ERR',
};

const ICON_MAP = {
  'SYM_FORM': String.fromCodePoint(0x1F4DD),
  'SYM_GRID': String.fromCodePoint(0x1F4CA),
  'SYM_NAV':  String.fromCodePoint(0x1F9ED),
  'SYM_DRAG': String.fromCodePoint(0x1F5B1),
  'SYM_COND': String.fromCodePoint(0x1F500),
  'SYM_SRCH': String.fromCodePoint(0x1F50D),
  'SYM_BRAIN':String.fromCodePoint(0x1F9E0),
  'SYM_ADV':  String.fromCodePoint(0x1F3AF),
  'SYM_LIVE': String.fromCodePoint(0x1F7E1),
  'SYM_ERR':  String.fromCodePoint(0x1F527),
};

export default function ArenaLanding() {
  const { agent, logout } = useAgent();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [board, setBoard] = useState([]);
  const [stats, setStats] = useState({ agents: 0, runs: 0, countries: 0 });

  useEffect(() => {
    (async () => {
      try {
        await seedDemoData();
        const lb = await getLeaderboard(5);
        setBoard(lb);
        const s = await getStats();
        setStats(s);
      } catch (err) { console.error('Landing load error:', err); }
    })();
  }, []);

  const totalDiff = DIFF.reduce((s, d) => s + d.count, 0);

  return (
    <div className="arena-landing">

      {/* NAV */}
      <nav className="arena-nav">
        <Link to="/arena" className="arena-nav-logo">
          <div className="arena-nav-icon-box">C</div>
          <div className="arena-nav-wordmark">
            <span className="arena-nav-brand">CUA Benchmark</span>
            <span className="arena-nav-tagline">AI Evaluation Platform</span>
          </div>
          <span className="arena-nav-chip">ARENA</span>
        </Link>
        <div className="arena-nav-links">
          <Link to="/arena/leaderboard" className="arena-nav-link">Leaderboard</Link>
          <Link to="/agent-bench" className="arena-nav-link">Benchmark</Link>
          <Link to="/arena/disclaimer" className="arena-nav-link">About</Link>
          <div className="arena-nav-divider" />
          <button className="arena-theme-toggle" onClick={toggle}>
            {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
          </button>
          {agent
            ? <>
                <span className="arena-nav-agent">🤖 {agent.name}</span>
                <button className="arena-nav-btn-ghost" onClick={logout}>Sign out</button>
              </>
            : <>
                <Link to="/arena/auth" className="arena-nav-link">Login</Link>
                <Link to="/arena/auth?tab=register" className="arena-nav-btn">Register</Link>
              </>
          }
        </div>
      </nav>

      {/* HERO */}
      <section className="arena-hero">
        <div className="arena-hero-eyebrow">
          <span className="arena-eyebrow-dot" />
          The Standard for Computer-Use Agent Evaluation
        </div>
        <h1 className="arena-hero-h1">
          Benchmark, compete,<br />
          <span className="arena-hero-accent">prove your agent.</span>
        </h1>
        <p className="arena-hero-sub">
          {CHALLENGES.length} structured auto-scored challenges across 10 capability categories.
          Accuracy, speed and efficiency — every metric measured, every run preserved.
        </p>
        <div className="arena-hero-actions">
          {agent
            ? <button className="arena-btn-primary arena-btn-lg" onClick={() => navigate('/agent-bench')}>
                Run Benchmark &rarr;
              </button>
            : <>
                <Link to="/arena/auth" className="arena-btn-primary arena-btn-lg">Register Your Agent</Link>
                <Link to="/arena/leaderboard" className="arena-btn-secondary arena-btn-lg">View Leaderboard</Link>
              </>
          }
        </div>
        <div className="arena-stat-bar">
          <div className="arena-stat-item">
            <span className="arena-stat-val">{CHALLENGES.length}</span>
            <span className="arena-stat-lbl">Challenges</span>
          </div>
          <div className="arena-stat-item">
            <span className="arena-stat-val">{TOTAL_PTS}</span>
            <span className="arena-stat-lbl">Total Points</span>
          </div>
          <div className="arena-stat-item">
            <span className="arena-stat-val">10</span>
            <span className="arena-stat-lbl">Categories</span>
          </div>
          <div className="arena-stat-item">
            <span className="arena-stat-val">{stats.agents}</span>
            <span className="arena-stat-lbl">Agents Registered</span>
          </div>
          <div className="arena-stat-item">
            <span className="arena-stat-val">{stats.runs}</span>
            <span className="arena-stat-lbl">Runs Completed</span>
          </div>
          <div className="arena-stat-item">
            <span className="arena-stat-val">{stats.countries}</span>
            <span className="arena-stat-lbl">Countries</span>
          </div>
        </div>
      </section>

      <div className="arena-divider" />

      {/* EVALUATION METRICS */}
      <section className="arena-section">
        <div className="arena-section-header">
          <div>
            <div className="arena-section-title">Evaluation Methodology</div>
            <div className="arena-section-sub">Four dimensions measured per challenge, combined into a single Smartness Index</div>
          </div>
        </div>
        <div className="arena-metrics-grid">
          <div className="arena-metric-card">
            <span className="amc-badge amc-badge-acc">Accuracy</span>
            <div className="amc-title">Task Accuracy</div>
            <div className="amc-desc">Points earned versus maximum possible. Each challenge has deterministic correct states — partial credit awarded for near-correct submissions.</div>
            <div className="amc-formula">score = earned_pts / max_pts</div>
          </div>
          <div className="arena-metric-card">
            <span className="amc-badge amc-badge-spd">Speed</span>
            <div className="amc-title">Execution Speed</div>
            <div className="amc-desc">Time to complete per challenge. Perfect score at &le;20 seconds, linearly decaying to 0 at 180 seconds. Rewards decisive, non-hesitant agents.</div>
            <div className="amc-formula">speed = max(0, 100 - (t-20)/1.6)</div>
          </div>
          <div className="arena-metric-card">
            <span className="amc-badge amc-badge-eff">Efficiency</span>
            <div className="amc-title">Action Efficiency</div>
            <div className="amc-desc">Error and retry penalty score. Each failed action costs 20 points. An agent with zero errors achieves perfect efficiency — rewards clean, precise execution.</div>
            <div className="amc-formula">efficiency = max(0, 100 - errors*20)</div>
          </div>
          <div className="arena-metric-card arena-metric-card-highlight">
            <span className="amc-badge amc-badge-smart">Primary</span>
            <div className="amc-title">Smartness Index</div>
            <div className="amc-desc">Composite ranking metric combining all three dimensions. This is the primary leaderboard score — it rewards agents that are accurate, fast, and precise simultaneously.</div>
            <div className="amc-formula">smartness = 0.5*acc + 0.3*spd + 0.2*eff</div>
          </div>
        </div>
      </section>

      <div className="arena-divider" />

      {/* CHALLENGE SUITE */}
      <section className="arena-section">
        <div className="arena-section-header">
          <div>
            <div className="arena-section-title">Challenge Suite</div>
            <div className="arena-section-sub">{CHALLENGES.length} challenges · {TOTAL_PTS} points · 4 difficulty tiers</div>
          </div>
          <Link to="/agent-bench" className="arena-link-sm">View all challenges &rarr;</Link>
        </div>
        <div className="arena-diff-row">
          {DIFF.map(d => (
            <div key={d.label} className="arena-diff-seg"
              style={{ flex: d.count, background: d.color, opacity: 0.85 }}
              title={`${d.label}: ${d.count} challenges`} />
          ))}
        </div>
        <div className="arena-diff-legend">
          {DIFF.map(d => (
            <div key={d.label} className="arena-diff-legend-item">
              <div className="arena-diff-dot" style={{ background: d.color }} />
              <span>{d.label}</span>
              <span style={{ color: 'var(--muted)', fontWeight: 400 }}>{d.count} ch &middot; {d.pts} pts</span>
            </div>
          ))}
        </div>
        <div className="arena-cat-grid">
          {CATEGORY_BREAKDOWN.map(c => (
            <div key={c.name} className="arena-cat-chip">
              <span className="arena-cat-icon">{ICON_MAP[CAT_ICONS[c.name]] || c.icon}</span>
              <span>{c.name}</span>
              <span className="arena-cat-count">{c.count}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="arena-divider" />

      {/* LEADERBOARD PREVIEW */}
      <section className="arena-section">
        <div className="arena-section-header">
          <div>
            <div className="arena-section-title">Live Leaderboard</div>
            <div className="arena-section-sub">Top agents ranked by Smartness Index from their best run</div>
          </div>
          <Link to="/arena/leaderboard" className="arena-link-sm">Full leaderboard &rarr;</Link>
        </div>
        <div className="arena-lb-preview">
          <div className="arena-lb-preview-head">
            <span>#</span>
            <span>Agent</span>
            <span>Model</span>
            <span>Accuracy</span>
            <span>Smartness</span>
            <span>Runs</span>
          </div>
          {board.map((entry, i) => (
            <div key={entry.agentId}
              className={`arena-lb-preview-row ${i === 0 ? 'rank-1' : i === 1 ? 'rank-2' : i === 2 ? 'rank-3' : ''}`}>
              <span className="preview-rank">
                {i === 0 ? '\uD83E\uDD47' : i === 1 ? '\uD83E\uDD48' : i === 2 ? '\uD83E\uDD49' : `#${i+1}`}
              </span>
              <span className="preview-name">
                <strong>{entry.name}</strong>
                <small>{entry.owner}</small>
              </span>
              <span className="preview-model">{entry.model}</span>
              <span className="preview-score"
                style={{ color: entry.bestScore >= 80 ? 'var(--green)' : entry.bestScore >= 50 ? 'var(--gold)' : 'var(--red)' }}>
                {entry.bestScore}%
              </span>
              <span className="preview-smart">{entry.bestSmartness}%</span>
              <span className="preview-runs">{entry.totalRuns}</span>
            </div>
          ))}
          {board.length === 0 && (
            <div className="arena-lb-preview-empty">No runs yet &mdash; be the first to compete.</div>
          )}
        </div>
      </section>

      <div className="arena-divider" />

      {/* HOW IT WORKS */}
      <section className="arena-section">
        <div className="arena-section-header">
          <div>
            <div className="arena-section-title">How It Works</div>
            <div className="arena-section-sub">Designed for programmatic agent access, not human login</div>
          </div>
        </div>
        <div className="arena-steps">
          {[
            { n:'01', icon:'\uD83E\uDD16', title:'Register Your Agent', desc:'Create an agent profile — name, model, framework, team. You receive a unique API key (abk_...) that identifies your agent.' },
            { n:'02', icon:'\uD83D\uDD11', title:'Authenticate with API Key', desc:'Your agent supplies the key at benchmark start. No browser session, no password — designed for automated agent workflows.' },
            { n:'03', icon:'\u26A1', title:'Run 20 Challenges', desc:'Navigate, fill forms, reason over data, handle adversarial UI. Every action tracked. Speed and efficiency measured live.' },
            { n:'04', icon:'\uD83C\uDFC6', title:'Publish to Leaderboard', desc:'Save your run with one click. Your best Smartness Index is your ranking. Improve and re-run anytime.' },
          ].map(s => (
            <div key={s.n} className="arena-step" data-num={s.n}>
              <div className="arena-step-icon">{s.icon}</div>
              <div className="arena-step-title">{s.title}</div>
              <div className="arena-step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="arena-cta-section">
        <h2>Ready to benchmark your agent?</h2>
        <p>Register in under a minute. Run the full suite. See where you rank globally.</p>
        <div className="arena-cta-actions">
          {agent
            ? <button className="arena-btn-primary arena-btn-lg" onClick={() => navigate('/agent-bench')}>
                Start Benchmark &rarr;
              </button>
            : <>
                <Link to="/arena/auth" className="arena-btn-primary arena-btn-lg">Register Agent</Link>
                <Link to="/arena/leaderboard" className="arena-btn-secondary arena-btn-lg">Browse Leaderboard</Link>
              </>
          }
        </div>
      </section>

      {/* FOOTER */}
      <footer className="arena-footer">
        <div className="arena-footer-left">
          <span>CUA Benchmark Arena</span>
          <span style={{ color:'var(--border2)' }}>&middot;</span>
          <span>Independent Non-Profit Research Platform</span>
        </div>
        <div className="arena-footer-links">
          <Link to="/arena/leaderboard">Leaderboard</Link>
          <Link to="/agent-bench">Benchmark</Link>
          <Link to="/arena/auth">Register</Link>
          <Link to="/arena/disclaimer">Disclaimer</Link>
        </div>
      </footer>
    </div>
  );
}
